import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SessionService } from '../../../_services/session/session.service';
import { Subscription } from 'rxjs';
import {
  resolveAdminCustomerId,
  resolveAdminCustomerRouteParamById,
} from '../../../_utils/route-param.utils';

interface AdminNavItem {
  id: string;
  labelKey: string;
  icon: string;
  route: string;
  absolute?: boolean;
  superuserOnly?: boolean;
  moduleIdentifier?: string;
}

interface AdminNavGroup {
  id: string;
  titleKey: string;
  items: AdminNavItem[];
}

@Component({
  standalone: true,
  selector: 'app-admin-root',
  templateUrl: './admin-root.component.html',
  styleUrls: ['./admin-root.component.scss'],
  imports: [CommonModule, RouterModule, TranslateModule],
})
export class AdminRootComponent implements OnInit, OnDestroy {
  public customers_id = 0;
  public customerRouteParam = '';
  public customerName = '';
  public sectionGroups: AdminNavGroup[] = [];
  private routeSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private session: SessionService,
  ) {}

  ngOnInit(): void {
    this.routeSub = this.route.paramMap.subscribe((params) => {
      const rawCustomerParam = `${params.get('customers_id') || ''}`.trim();
      const normalizedCustomersId =
        this.resolveRouteCustomerId(rawCustomerParam);

      if (normalizedCustomersId <= 0) {
        this.router.navigate(['/administration'], { replaceUrl: true });
        return;
      }

      this.customers_id = normalizedCustomersId;
      this.customerRouteParam = this.resolveRouteCustomerParam(
        rawCustomerParam,
        normalizedCustomersId,
      );
      this.resolveCustomer();
      this.buildNavigation();

      const canonicalPrefix = `/administration/${this.customerRouteParam}`;
      const isAlreadyCanonical = this.router.url.startsWith(canonicalPrefix);
      if (!isAlreadyCanonical && this.customerRouteParam) {
        const suffix = this.router.url.replace(/^\/administration\/[^/]+/, '');
        this.router.navigateByUrl(`${canonicalPrefix}${suffix || '/users'}`, {
          replaceUrl: true,
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  isActive(item: AdminNavItem): boolean {
    if (item.absolute) {
      return this.router.url.startsWith(item.route);
    }

    const chunks = item.route.split('/').filter(Boolean);
    const routeCustomerParam =
      this.customerRouteParam || `${this.customers_id}`;
    const absoluteRoute = `/administration/${routeCustomerParam}/${chunks.join('/')}`;
    if (chunks.length > 1) {
      const sectionRoute = `/administration/${routeCustomerParam}/${chunks[0]}`;
      return this.router.url.startsWith(sectionRoute);
    }

    return this.router.url.startsWith(absoluteRoute);
  }

  canShowItem(item: AdminNavItem): boolean {
    if (!item.superuserOnly) {
      if (!item.moduleIdentifier) {
        return true;
      }

      return this.isCommercialModuleEnabled(item.moduleIdentifier);
    }

    return this.session.isSuperUser();
  }

  toRouterLink(item: AdminNavItem): any[] | string {
    if (item.absolute) {
      return item.route;
    }

    const chunks = item.route.split('/').filter(Boolean);
    const routeCustomerParam =
      this.customerRouteParam || `${this.customers_id}`;
    return ['/administration', routeCustomerParam, ...chunks];
  }

  private resolveCustomer() {
    const permissions = this.session.getPermissions();
    const customer = permissions.find((item) => +item.id === this.customers_id);
    this.customerName = customer?.name || '';

    if (customer) {
      this.session.setCustomer(customer as any);
    }
  }

  private buildNavigation() {
    this.sectionGroups = [
      {
        id: 'team',
        titleKey: 'ADMIN.WORKSPACE.NAV.TEAM',
        items: [
          {
            id: 'users',
            labelKey: 'ADMIN.WORKSPACE.NAV.USERS',
            icon: 'fas fa-users',
            route: 'users',
          },
          {
            id: 'user-invitations',
            labelKey: 'ADMIN.WORKSPACE.NAV.PENDING_INVITATIONS',
            icon: 'fas fa-envelope-open-text',
            route: 'invitations',
          },
          {
            id: 'roles',
            labelKey: 'ADMIN.WORKSPACE.NAV.ROLES',
            icon: 'fas fa-user-shield',
            route: 'roles',
          },
        ],
      },
      {
        id: 'operations',
        titleKey: 'ADMIN.WORKSPACE.NAV.OPERATIONS',
        items: [
          {
            id: 'sites',
            labelKey: 'ADMIN.WORKSPACE.NAV.SITES',
            icon: 'fas fa-clinic-medical',
            route: 'clinics',
          },
          {
            id: 'schedules',
            labelKey: 'ADMIN.WORKSPACE.NAV.SCHEDULES',
            icon: 'fas fa-calendar-alt',
            route: 'schedules',
          },
          {
            id: 'patients',
            labelKey: 'ADMIN.WORKSPACE.NAV.PATIENTS',
            icon: 'fas fa-hospital-user',
            route: 'patients',
          },
          {
            id: 'nutrition-settings',
            labelKey: 'ADMIN.WORKSPACE.NAV.NUTRITION_SETTINGS',
            icon: 'fas fa-seedling',
            route: 'nutrition-settings',
            moduleIdentifier: 'nutrition',
          },
          {
            id: 'clinical-history-structures',
            labelKey: 'ADMIN.WORKSPACE.NAV.CLINICAL_HISTORY_STRUCTURES',
            icon: 'fas fa-folder-tree',
            route: 'clinical-history-structures',
          },
        ],
      },
      {
        id: 'catalog',
        titleKey: 'ADMIN.WORKSPACE.NAV.CATALOG',
        items: [
          {
            id: 'treatments',
            labelKey: 'ADMIN.WORKSPACE.NAV.TREATMENTS',
            icon: 'fas fa-notes-medical',
            route: 'treatments',
          },
          {
            id: 'products',
            labelKey: 'ADMIN.WORKSPACE.NAV.PRODUCTS',
            icon: 'fas fa-box-open',
            route: 'products',
            moduleIdentifier: 'sales',
          },
          {
            id: 'jobs',
            labelKey: 'ADMIN.WORKSPACE.NAV.JOBS',
            icon: 'fas fa-briefcase',
            route: 'jobs',
          },
          {
            id: 'marketing',
            labelKey: 'ADMIN.WORKSPACE.NAV.MARKETING',
            icon: 'fas fa-bullhorn',
            route: 'marketing-channels',
          },
        ],
      },
      {
        id: 'account',
        titleKey: 'ADMIN.WORKSPACE.NAV.ACCOUNT',
        items: [
          {
            id: 'modules',
            labelKey: 'ADMIN.SUBSCRIPTION.TAB_MARKETPLACE',
            icon: 'fas fa-puzzle-piece',
            route: 'modules',
          },
          {
            id: 'subscription',
            labelKey: 'ADMIN.SUBSCRIPTION.TAB_SUBSCRIPTION',
            icon: 'fas fa-credit-card',
            route: 'subscription',
          },
          {
            id: 'history',
            labelKey: 'ADMIN.SUBSCRIPTION.TAB_HISTORY',
            icon: 'fas fa-history',
            route: 'history',
          },
        ],
      },
      {
        id: 'governance',
        titleKey: 'ADMIN.WORKSPACE.NAV.GOVERNANCE',
        items: [
          {
            id: 'logs',
            labelKey: 'ADMIN.WORKSPACE.NAV.LOGS',
            icon: 'fas fa-history',
            route: 'logs',
          },
          {
            id: 'data',
            labelKey: 'ADMIN.WORKSPACE.NAV.DATA',
            icon: 'fas fa-database',
            route: 'data/export/appointments',
          },
          {
            id: 'customers',
            labelKey: 'ADMIN.WORKSPACE.NAV.CUSTOMERS',
            icon: 'fas fa-building',
            route: '/administration/customers',
            absolute: true,
            superuserOnly: true,
          },
        ],
      },
    ];
  }

  private isCommercialModuleEnabled(identifier: string): boolean {
    const normalized = `${identifier || ''}`.trim().toLowerCase();
    const moduleIdentifier =
      normalized === 'statistics' ? 'analytics' : normalized;

    if (
      moduleIdentifier === 'appointments' ||
      moduleIdentifier === 'patients'
    ) {
      return true;
    }

    const permissions = this.session.getPermissions() || [];
    const customer = permissions.find(
      (item) => +item.id === +this.customers_id,
    );
    const modules = customer?.commercialModules as any;
    if (!modules) {
      return true;
    }

    return modules?.[moduleIdentifier] !== false;
  }

  private resolveRouteCustomerId(rawCustomerParam: string): number {
    const parsed = resolveAdminCustomerId(rawCustomerParam);
    if (parsed > 0) {
      return parsed;
    }

    const contextCustomerId = +(this.session.getEffectiveCustomerId() || 0);
    if (contextCustomerId > 0) {
      return contextCustomerId;
    }

    const customerAdmin = (this.session.getPermissions() || []).find(
      (customer) =>
        this.session.hasCustomerPermission(
          customer as any,
          'ADMIN_PANEL.ACCESS',
        ),
    );
    if (customerAdmin?.id) {
      return +customerAdmin.id;
    }

    const firstCustomer = (this.session.getPermissions() || [])[0];
    return +(firstCustomer?.id || 0);
  }

  private resolveRouteCustomerParam(
    rawCustomerParam: string,
    normalizedCustomersId: number,
  ): string {
    return (
      resolveAdminCustomerRouteParamById(
        normalizedCustomersId,
        rawCustomerParam,
      ) || `${normalizedCustomersId}`
    );
  }
}
